import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useCart } from '../../context/CartContext';
import logo from '../../assets/logo-getit.png';
import logoPosgo from '../../assets/logo-posgo.png';
import ConfirmCancelModal from '../shared/ConfirmCancelModal';

const getMimeType = (b64) => b64?.startsWith('iVBOR') ? 'image/png' : 'image/jpeg';

const SubcategorySelectionScreen = () => {
    const { categories, sessionData, categoryImages } = useAppContext();
    const { items, clearCart } = useCart();
    const [showConfirm, setShowConfirm] = useState(false);
    const navigate = useNavigate();
    const { categoryId } = useParams();
    const totalItems = items.reduce((sum, item) => sum + (item.quantity || 1), 0);

    const currentCategory = useMemo(() => {
        return categories?.find(cat => cat.id === parseInt(categoryId));
    }, [categories, categoryId]);

    const subcategories = useMemo(() => {
        if (!sessionData?.products || !categoryId) return [];

        const productsInCategory = sessionData.products.filter(
            product => product.is_totem && product.category_id === parseInt(categoryId)
        );

        const subcategoryMap = new Map();
        productsInCategory.forEach(product => {
            if (product.subcategory_id && product.subcategory_name) {
                const existing = subcategoryMap.get(product.subcategory_id);
                const price = parseFloat(product.price) || Infinity;
                if (!existing || price < existing.minPrice) {
                    subcategoryMap.set(product.subcategory_id, {
                        name: product.subcategory_name,
                        minPrice: price
                    });
                }
            }
        });

        return Array.from(subcategoryMap, ([id, { name, minPrice }]) => ({ id, name, minPrice }))
            .sort((a, b) => a.minPrice - b.minPrice);
    }, [sessionData, categoryId]);

    const handleSubcategoryClick = (subcategoryId) => {
        navigate(`/products/${categoryId}/${subcategoryId}`);
    };

    const handleShowAll = () => {
        navigate(`/products/${categoryId}`);
    };

    const handleBackToCategories = () => {
        navigate('/');
    };

    return (
        <>
        <div className="subcategory-screen">
            <div className="subcategory-header">
                <img src={logo} alt="GetIT" className="logo" />
                <div className="posgo-badge">
                    <img src={logoPosgo} alt="PosGo" className="posgo-logo" />
                    <span className="posgo-text">PosGo.cl</span>
                </div>
            </div>

            <div className="subcategory-body">
                <h1 className="category-title">{currentCategory?.name}</h1>
                <p className="category-subtitle">Selecciona una subcategoría</p>

                <div className="subcategory-grid">
                    {subcategories.map((subcategory) => (
                        <button
                            key={subcategory.id}
                            className="subcategory-card"
                            onClick={() => handleSubcategoryClick(subcategory.id)}
                        >
                            <div className="subcategory-image">
                                {categoryImages[subcategory.id] && (
                                    <img src={`data:${getMimeType(categoryImages[subcategory.id])};base64,${categoryImages[subcategory.id]}`} alt="" />
                                )}
                            </div>
                            <div className="subcategory-name">{subcategory.name}</div>
                        </button>
                    ))}
                </div>

                <button className="btn-all" onClick={handleShowAll}>
                    Ver Todos los Productos de {currentCategory?.name}
                </button>
            </div>

            <div className="subcategory-footer">
                <button className="btn-back" onClick={handleBackToCategories}>
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Volver al menú
                </button>
                {totalItems > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <button className="btn-cancel" onClick={() => setShowConfirm(true)}>
                            Cancelar todo
                        </button>
                        <button className="btn-pay" onClick={() => navigate('/payment')}>
                            Ir a pagar ({totalItems})
                            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </button>
                    </div>
                )}
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@900&display=swap');

                * {
                    box-sizing: border-box;
                }

                .subcategory-screen {
                    min-height: 100vh;
                    display: flex;
                    flex-direction: column;
                    background: #f5f5f5;
                    overflow-x: hidden;
                }

                .subcategory-header {
                    background: white;
                    padding: 1.5rem;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    position: relative;
                }

                .posgo-badge {
                    position: absolute;
                    right: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                }

                .posgo-logo {
                    height: 80px;
                    width: auto;
                }

                .posgo-text {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #db2113;
                    font-family: Quicksand, sans-serif;
                }

                .logo {
                    height: 100px;
                    width: auto;
                }

                .subcategory-body {
                    flex: 1;
                    padding: 1.5rem 1.5rem 6rem;
                    max-width: 1800px;
                    width: 100%;
                    margin: 0 auto;
                    box-sizing: border-box;
                }

                .subcategory-footer {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background: white;
                    padding: 0.75rem 2rem;
                    box-shadow: 0 -4px 12px rgba(0,0,0,0.08);
                    border-top: 2px solid #e5e7eb;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    z-index: 40;
                }

                .btn-back {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 2rem;
                    background: #0E8164;
                    color: white;
                    border: none;
                    border-radius: 50px;
                    font-size: 1.1rem;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.2s;
                    white-space: nowrap;
                }

                .btn-back:hover {
                    background: #0a6b52;
                }

                .category-title {
                    font-family: 'Montserrat', sans-serif;
                    font-size: 3rem;
                    font-weight: 900;
                    color: #0E8164;
                    text-align: center;
                    margin-bottom: 0.5rem;
                    letter-spacing: -0.02em;
                }

                .category-subtitle {
                    font-size: 1.25rem;
                    color: #666;
                    text-align: center;
                    margin-bottom: 3rem;
                }

                .subcategory-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 2.5rem;
                    margin-bottom: 2rem;
                }

                .subcategory-card {
                    background: white;
                    border: 3px solid transparent;
                    border-radius: 20px;
                    padding: 1.5rem 1rem;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 1rem;
                    min-height: 200px;
                    box-sizing: border-box;
                    min-width: 0;
                }

                .subcategory-card:hover {
                    border-color: #0E8164;
                    transform: translateY(-6px);
                    box-shadow: 0 12px 24px rgba(14, 129, 100, 0.2);
                }

                .subcategory-image {
                    width: min(200px, 80%);
                    aspect-ratio: 1;
                    border-radius: 16px;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .subcategory-image img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .subcategory-name {
                    font-size: 1.875rem;
                    font-weight: 700;
                    color: #1a1a1a;
                    text-align: center;
                    line-height: 1.3;
                }

                .btn-all {
                    display: block;
                    margin: 0 auto 2.5rem;
                    background: #DC2626;
                    color: white;
                    border: none;
                    border-radius: 12px;
                    padding: 1.25rem 4rem;
                    font-size: 1.5rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 16px rgba(220, 38, 38, 0.3);
                }

                .btn-all:hover {
                    background: #B91C1C;
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(220, 38, 38, 0.4);
                }

                @media (max-width: 1400px) {
                    .subcategory-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 2rem;
                    }
                }

                @media (max-width: 1024px) {
                    .subcategory-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }

                @media (max-width: 768px) {
                    .subcategory-body {
                        padding: 2rem 1.5rem;
                    }

                    .subcategory-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 1.5rem;
                    }

                    .subcategory-card {
                        min-height: 280px;
                    }

                    .subcategory-image {
                        width: 160px;
                        height: 160px;
                    }

                    .subcategory-name {
                        font-size: 1.5rem;
                    }

                    .btn-all {
                        width: 100%;
                        padding: 1.5rem 3rem;
                        font-size: 1.5rem;
                    }
                }

                .btn-cancel {
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                    background: white;
                    color: #DC2626;
                    border: 2px solid #DC2626;
                    border-radius: 50px;
                    padding: 0.65rem 1.4rem;
                    font-size: 0.9rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s;
                    white-space: nowrap;
                }

                .btn-cancel:hover {
                    background: #fee2e2;
                }

                .btn-pay {
                    display: flex;
                    align-items: center;
                    gap: 0.6rem;
                    background: #DC2626;
                    color: white;
                    border: none;
                    border-radius: 50px;
                    padding: 0.75rem 2rem;
                    font-size: 1.1rem;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.2s;
                    white-space: nowrap;
                }

                .btn-pay:hover {
                    background: #B91C1C;
                }
            `}</style>
        </div>

        <ConfirmCancelModal
            isOpen={showConfirm}
            onConfirm={() => { setShowConfirm(false); clearCart(); navigate('/'); }}
            onCancel={() => setShowConfirm(false)}
        />
        </>
    );
};

export default SubcategorySelectionScreen;